import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import client from "@/lib/api";
import { MessageSquare, Send } from "lucide-react";

/** Conversational coach — opens a chat with the AI to diagnose an off-balance close. */
export default function CoachDialog({ closeId, contextText, open, onOpenChange, onFinish }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      send("Let's figure this out. Ask me the first question.", true);
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text, primer = false) => {
    if (!text.trim() || busy) return;
    const next = primer ? [{ role: "user", content: text }] : [...messages, { role: "user", content: text }];
    if (!primer) setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const url = closeId ? `/closes/${closeId}/coach` : `/coach`;
      const body = closeId ? { messages: next } : { messages: next, context: contextText };
      const { data } = await client.post(url, body);
      setMessages([...next, data]);
      if (data.content?.toLowerCase().includes("best guess") && onFinish) onFinish(data.content);
    } catch {
      setMessages([...next, { role: "assistant", content: "Sorry — couldn't reach the coach. Try again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[#FDFBF7] border-[#E8E4DA] p-0 overflow-hidden">
        <DialogTitle className="px-4 pt-4 text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>
          Talk it through
        </DialogTitle>
        <DialogDescription className="px-4 text-[#5C7566] text-xs">
          Answer a few short questions — the assistant will help narrow down the cause.
        </DialogDescription>
        <div ref={scrollRef} data-testid="coach-thread" className="px-4 py-3 max-h-[60vh] overflow-y-auto space-y-2">
          {messages.filter((m) => m.role !== "user" || messages.indexOf(m) > 0).map((m, i) => (
            <div key={i} className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "assistant" ? "bg-white border border-[#E8E4DA] text-[#2B533E]" : "bg-[#1A5D3A] text-white ml-8"}`}>
              {m.content}
            </div>
          ))}
          {busy && <div className="text-xs text-[#5C7566]">Thinking…</div>}
        </div>
        <div className="border-t border-[#E8E4DA] p-3 flex gap-2 bg-white">
          <input
            data-testid="coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send(input)}
            placeholder="Type your answer…"
            className="flex-1 rounded-xl border border-[#E8E4DA] px-3 py-2 text-sm outline-none focus:border-[#1A5D3A]"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            data-testid="coach-send"
            onClick={() => send(input)}
            disabled={busy || !input.trim()}
            className="h-10 w-10 rounded-xl bg-[#1A5D3A] text-white flex items-center justify-center disabled:opacity-40"
          >
            <Send size={16} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CoachButton({ closeId, contextText, label = "Talk to the assistant" }) {
  const [open, setOpen] = useState(false);
  if (!closeId && !contextText) return null;
  return (
    <>
      <button
        data-testid="coach-open"
        onClick={() => setOpen(true)}
        className="w-full h-12 rounded-2xl bg-[#0D311E] text-[#FDFBF7] flex items-center justify-center gap-2 active:scale-[0.99] mt-2"
      >
        <MessageSquare size={16} /> {label}
      </button>
      <CoachDialog closeId={closeId} contextText={contextText} open={open} onOpenChange={setOpen} />
    </>
  );
}
